import * as angular from 'angular';
import * as moment from 'moment';

import { refreshClick } from '../../common/events';
import { IDateRangePickerOptions, Interval, ComponentDefinition, IApplicationConfiguration, IDatePickerDates } from '../../models';
import { IDateRangeService, IIntervalService } from '../../services/general';
import { ICurrentApplicationService } from '../../services/data';
import { IDateAlignmentHelperService, IDateRangeOptionsBuilder, IDateRangePickerService } from '../../services/helpers';
import { momentFormat } from '../../common';

class FixedIntervalPickerController implements angular.IComponentController, angular.IOnChanges {
    date: moment.Moment;
    options: IDateRangePickerOptions;
    showRefresh: boolean;

    readonly format: string;
    readonly initialDate: string;
    readonly interval: Interval;
    readonly needsRefresh?: { isRequired: boolean; message: string };
    readonly preventFuture: boolean;

    private _config: IApplicationConfiguration;
    private _range: IDatePickerDates;
    private _withTime = ['s', 'm', 'h'];
    private _intervalUnitGreaterThanDay = ['w', 'M', 'Q', 'y'];
    private _watchers = [];

    static $inject = [
        '$rootScope',
        '$timeout',
        'dateRangeService',
        'dateRangePickerService',
        'dateRangeOptionsBuilder',
        'currentApplicationService',
        'dateAlignmentHelperService',
        'intervalService'
    ];
    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _dateRangeService: IDateRangeService,
        private _dateRangePickerService: IDateRangePickerService,
        private _optionsBuider: IDateRangeOptionsBuilder,
        private _currentApplicationService: ICurrentApplicationService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _intervalService: IIntervalService
    ) {
        this.showRefresh = true;
    }

    $onInit() {
        this._config = this._currentApplicationService.getApplicationConfiguration();
        this.options = this.setOptions();
        this.onInitialDate();
        this._watchers.push(
            this._$rootScope.$on('$translateChangeEnd', () => {
                this.options = this.setOptions();
            })
        );
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if ((onChangesObj['initialDate']?.currentValue || onChangesObj['interval']?.currentValue) && this._config) {
            this.onInitialDate();
        }
    }

    $onDestroy() {
        this._watchers.forEach((w) => w());
    }

    private onInitialDate() {
        const unit = this.interval.unit;
        const wantedDate = this.initialDate
            ? moment(this.initialDate)
            : this._dateRangeService.initialized()
            ? moment(this._dateRangeService.beginDate() as string)
            : moment().startOf(unit);
        this.date = this.align(wantedDate);
        this.setRange(this.date);
        this.update(this._range.startDate, this._range.endDate, false, false);
    }

    private setOptions() {
        const timeIncrement = this.interval.unit === 'h' ? 60 : this.interval.unit === 'm' ? this.interval.interval : 1;
        const options: IDateRangePickerOptions = {
            applyClass: 'btn-primary',
            timePicker: this._withTime.indexOf(this.interval.unit) >= 0,
            autoApply: true,
            autoUpdateInput: true,
            timePicker24Hour: true,
            singleDatePicker: true,
            showDropdowns: true,
            timePickerIncrement: timeIncrement,
            locale: {
                // #41469 decided to always show full datetime regardless frequency
                format: this.format || momentFormat,
                firstDay: this._config.weekBeginDay
            },
            eventHandlers: {
                'apply.daterangepicker': () => {
                    if (this.isFrequencyAtLeastOneDay()) {
                        const offsetBefore = this.date.utcOffset();
                        this.date = this.date.clone().add(this._config.beginDayTime);
                        const offsetAfter = this.date.utcOffset();
                        if (offsetBefore !== offsetAfter) {
                            this.date.add(offsetBefore - offsetAfter, 'minute');
                        }
                    }
                    const aligned = this.align(this.date);
                    this.date = aligned;
                    this.setRange(aligned);
                    this.update(this._range.startDate, this._range.endDate);
                }
            }
        };
        if (this.preventFuture) {
            options.maxDate = moment();
        }
        return this._optionsBuider.translate(options);
    }

    private align(date: moment.Moment): moment.Moment {
        if (this._config) {
            const { interval, unit } = this.interval;
            const beginMoment = moment(date.toISOString());
            let alignedDate: moment.Moment;

            if (this.isFrequencyGreaterThanOneDay()) {
                alignedDate = this._dateAlignmentHelperService.previous(
                    beginMoment,
                    { interval, intervalTimeUnit: this._intervalService.getTimeUnit(unit) },
                    this._config.beginDayTime,
                    this._config.weekBeginDay,
                    this._config.timeZone,
                    false
                );
            } else {
                alignedDate = this._dateAlignmentHelperService.next(
                    beginMoment,
                    { interval, intervalTimeUnit: this._intervalService.getTimeUnit(unit) },
                    this._config.beginDayTime,
                    this._config.weekBeginDay,
                    this._config.timeZone
                );
            }

            if ((unit === 'h' || unit === 'm' || unit === 's') && alignedDate.isAfter(date)) {
                alignedDate = alignedDate.subtract(interval, unit);
            }
            return alignedDate;
        } else {
            return date;
        }
    }

    private isFrequencyGreaterThanOneDay(): boolean {
        return this._intervalUnitGreaterThanDay.indexOf(this.interval.unit) >= 0 || (this.interval.unit === 'd' && this.interval.interval > 1);
    }

    private isFrequencyAtLeastOneDay(): boolean {
        return this._intervalUnitGreaterThanDay.indexOf(this.interval.unit) >= 0 || (this.interval.unit === 'd' && this.interval.interval >= 1);
    }

    private setRange(aligned: moment.Moment) {
        if (this._config) {
            // input is aligned already
            const beginMoment = aligned.clone();
            let endMoment = beginMoment.clone().add(this.interval.interval, this.interval.unit);
            if (this.preventFuture) {
                const today = endMoment.subtract(this._config.beginDayTime).startOf('day');
                //TODO
                if (endMoment > today) {
                    endMoment = today;
                }
            }

            const dates = { startDate: beginMoment, endDate: endMoment };
            if (!this._dateRangePickerService.isAligned(dates, this._intervalService.getFrequency(this.interval), this._config)) {
                // TODO we must enforce freq from the group !!!!
                const enforcedRange = this._dateRangePickerService.enforceAlign(dates, this._intervalService.getFrequency(this.interval), this._config);
                this._range = {
                    startDate: enforcedRange.startDate,
                    endDate: enforcedRange.endDate
                };
            } else {
                this._range = dates;
            }
        }
    }

    refresh() {
        if ((!this.preventFuture || moment().isAfter(this.date)) && this._config) {
            const aligned = this.align(this.date);
            this.date = aligned;
            this.setRange(aligned);
            this.update(this._range.startDate, this._range.endDate, true);
        }
    }

    previous() {
        let newPick = this.date.clone().subtract(this.interval.interval, this.interval.unit);
        if (this.preventFuture) {
            while (newPick.isAfter(moment())) {
                newPick = newPick.subtract(this.interval.interval, this.interval.unit);
            }
        }
        this.setDate(newPick.toISOString());
        this.setRange(newPick);
        this.update(this._range.startDate, this._range.endDate, false);
    }

    next() {
        const newPick = this.date.clone().add(this.interval.interval, this.interval.unit);
        if (!this.preventFuture || moment().isAfter(newPick)) {
            this.setDate(newPick.toISOString());
            this.setRange(newPick);
            this.update(this._range.startDate, this._range.endDate, false);
        }
    }

    private setDate(date: string) {
        if (date) {
            this.options = null;
            this._$timeout(() => {
                this.date = moment(date);
                this.options = this.setOptions();
            });
        }
    }

    private update(beginMoment: moment.Moment, endMoment: moment.Moment, force: boolean = false, doRefresh: boolean = true) {
        const beginDate = beginMoment.toISOString();
        const endDate = endMoment.toISOString();
        this._dateRangeService.beginDate(beginDate);
        this._dateRangeService.endDate(endDate);
        this._dateRangeService.initialized(true);
        if (doRefresh) {
            this._$rootScope.$broadcast(refreshClick, {
                beginDate: beginDate,
                endDate: endDate,
                force: force
            });
        }
    }
}

export const wtFixedIntervalPicker: ComponentDefinition = {
    componentId: 'wtFixedIntervalPicker',
    controller: FixedIntervalPickerController,
    controllerAs: 'vm',
    template: require('./fixedIntervalPicker.component.html'),
    bindings: {
        disabled: '=?',
        format: '<?',
        initialDate: '<?',
        interval: '<',
        needsRefresh: '<?',
        preventFuture: '<?'
    }
};
